import { normalizeText } from './utils.js';

/* «Recomendado para ti» y «Porque viste X»: puntúa los títulos que el espectador no ha visto con lo que sí ha visto y guardado.
   No hay datos de otros usuarios (no son «tendencias»): la afinidad sale solo de su propio historial. Señales:
   - categoría del proveedor y géneros (de la lista, o de la ficha abierta si es más completa),
   - director y reparto (de las fichas que ya se abrieron: TMDB),
   - sagas (Rocky II tras ver Rocky),
   - cuándo se vio (lo reciente pesa más) y si se abandonó enseguida (resta un poco).
   Módulo puro, sin DOM ni estado. */

const GENERIC = new Set(['', 'n/a', 'na', 'none', 'otros', 'other', 'varios', 'various', 'general']);
const DAY = 86400000;

/** "Acción, Ciencia ficción / Thriller" → ['accion', 'ciencia ficcion', 'thriller'] */
export function genreTokens(genre) {
    if (!genre) return [];
    const out = [];
    String(genre).split(/[,/|;·&]+/).forEach((part) => {
        const token = normalizeText(part).trim();
        if (token.length > 1 && !GENERIC.has(token) && out.indexOf(token) === -1) out.push(token);
    });
    return out;
}

const catKey = (kind, item) => (item.categoryId ? kind + ':' + item.categoryId : '');

// Palabras que no son un título: «Película 3» y «Película 7» no son secuelas una de otra.
const GENERIC_TITLE = /^(parte|part|capitulo|chapter|vol|volumen|temporada|season|episodio|episode|peli|pelicula|pelis|movie|movies|film|films|serie|series|show|shows|cine|canal|channel|video|videos|documental|documentary)$/;
const ROMAN = /^(i{1,3}|iv|v|vi{0,3}|ix|x)$/;
/** «Rocky IV (1985)» → 'rocky'; «Matrix Reloaded» → 'matrix reloaded'. Se usa para encontrar sagas por nombre. */
export function franchiseKey(name) {
    let n = normalizeText(String(name || '').replace(/^[A-Za-z]{2,3}\s*[|:\-]\s*/, '')).replace(/\(?\b(19|20)\d{2}\b\)?/g, ' ');
    n = n.replace(/\b(4k|uhd|fhd|hd|sd|1080p|720p|dual|latino|castellano|vose|vos)\b/g, ' ').replace(/[^a-z0-9 ]+/g, ' ');
    const parts = n.split(/\s+/).filter(Boolean);
    // quita el número de entrega al final: «rocky 2», «rocky iv», «parte 3»
    while (parts.length > 1 && (/^\d{1,2}$/.test(parts[parts.length - 1]) || ROMAN.test(parts[parts.length - 1]) || /^(parte|part|capitulo|chapter|vol|volumen)$/.test(parts[parts.length - 1]))) parts.pop();
    // y lo que va tras dos puntos o guion (subtítulo) ya se quitó al limpiar signos: se queda con las 2 primeras palabras si es largo
    const key = parts.join(' ');
    // Una sola palabra que no es un título («parte», «capítulo»…) no identifica ninguna saga.
    if (GENERIC_TITLE.test(key)) return '';
    return key.length >= 4 ? key : '';
}

const EMPTY = Object.freeze([]);
const LIST_FEAT = new WeakMap(); // título → { cat, genres } sacados de la lista del proveedor (no cambian)
const TEXT_OF = new WeakMap();
const FRAN_OF = new WeakMap();

function listFeat(kind, item) {
    let f = LIST_FEAT.get(item);
    if (!f) { f = { cat: catKey(kind, item), genres: genreTokens(item.genre) }; LIST_FEAT.set(item, f); }
    return f;
}

/** Rellena la caché de características de un título (para hacerlo por trozos, antes de que haga falta). */
export function warmFeatures(kind, item) {
    listFeat(kind, item);
}

/** Texto normalizado del título (el que ya trae precalculado la lista, o el nombre) para filtrar rápido por saga. */
function textOf(item) {
    if (typeof item._s === 'string') return item._s;
    let t = TEXT_OF.get(item);
    if (t === undefined) { t = normalizeText(item.name); TEXT_OF.set(item, t); }
    return t;
}

function franOf(item) {
    let k = FRAN_OF.get(item);
    if (k === undefined) { k = franchiseKey(item.name); FRAN_OF.set(item, k); }
    return k;
}

const escapeRe = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** Expresión que dice, sin normalizar nada, si un texto puede pertenecer a alguna de esas sagas (filtro previo barato). */
function stemRegex(keys) {
    const list = Array.from(keys).filter(Boolean);
    return list.length ? new RegExp(list.map(escapeRe).join('|')) : null;
}

/** Características de un título: las de la lista y, si se conocen, las de su ficha (`extra`: { genre, cast[], director[] }). */
export function featuresOf(kind, item, extra) {
    const base = listFeat(kind, item);
    if (!extra) return { cat: base.cat, genres: base.genres, cast: EMPTY, director: EMPTY };
    return {
        cat: base.cat,
        genres: genreTokens([item.genre, extra.genre].filter(Boolean).join(',')),
        cast: (extra.cast || []).map(normalizeText).filter(Boolean),
        director: (extra.director || []).map(normalizeText).filter(Boolean)
    };
}

/** Cuánto cuenta lo visto/guardado: según cuánto se vio, lo reciente y si se dejó a los pocos minutos. Compatible con `weight` fijo. */
export function seedWeight(seed, now = Date.now()) {
    if (typeof seed.weight === 'number' && seed.at === undefined && seed.pct === undefined && seed.fav === undefined) return seed.weight;
    let w = 0;
    const pct = Number(seed.pct) || 0;
    if (seed.finished) w = 2.5;
    else if (pct >= 50) w = 2;
    else if (pct >= 10) w = 1;
    else if (pct > 0) w = 0.4;
    const age = seed.at ? Math.max(0, now - seed.at) : 0;
    // Dejado a los pocos minutos y hace más de unas horas: señal (suave) de que no gustó.
    if (!seed.finished && pct > 0 && pct < 8 && age > 6 * 3600000) w = -0.5;
    if (seed.fav) w += 1.5;
    if (w <= 0) return w;
    const recency = 0.25 + 0.75 * Math.pow(0.5, age / (90 * DAY));
    return w * recency;
}

function profile(seeds, featOf, now) {
    const p = { cats: new Map(), genres: new Map(), cast: new Map(), dirs: new Map(), fran: new Map(), total: 0 };
    seeds.forEach((s) => {
        const w = seedWeight(s, now);
        if (!w) return;
        const f = featOf(s.kind, s.item);
        const bump = (m, k) => m.set(k, (m.get(k) || 0) + w);
        if (w > 0) p.total += w;
        if (f.cat) bump(p.cats, f.cat);
        f.genres.forEach((g) => bump(p.genres, g));
        f.cast.slice(0, 6).forEach((c) => bump(p.cast, c));
        f.director.forEach((d) => bump(p.dirs, d));
        const fk = franchiseKey(s.item.name);
        if (fk && w > 0) bump(p.fran, fk);
    });
    return p;
}

function affinity(c, f, p, stemRe) {
    const t = p.total;
    const catScore = f.cat ? Math.max(0, (p.cats.get(f.cat) || 0) / t) : 0;
    let genreScore = 0;
    for (let i = 0; i < f.genres.length; i++) genreScore += (p.genres.get(f.genres[i]) || 0) / t;
    if (f.genres.length) genreScore /= Math.sqrt(f.genres.length);
    let dirScore = 0;
    for (let i = 0; i < f.director.length; i++) dirScore = Math.max(dirScore, (p.dirs.get(f.director[i]) || 0) / t);
    let castScore = 0;
    const nc = Math.min(6, f.cast.length);
    for (let i = 0; i < nc; i++) castScore += (p.cast.get(f.cast[i]) || 0) / t;
    if (nc) castScore /= Math.sqrt(nc);
    let sagaScore = 0;
    // Solo los títulos cuyo texto contiene el nombre de alguna saga vista se miran de verdad (150 000 títulos × normalizar = lento).
    if (stemRe && stemRe.test(textOf(c.item))) {
        const fk = franOf(c.item);
        if (fk && p.fran.has(fk)) sagaScore = Math.min(1, (p.fran.get(fk) || 0) / t * 4);
    }
    return Math.max(0, catScore * 0.6 + Math.max(0, genreScore) * 1.5 + dirScore * 1.4 + castScore * 0.9) + sagaScore * 0.8;
}

/**
 * seeds:      [{ kind, item, weight | at, pct, finished, fav }] lo visto o guardado.
 * candidates: [{ kind, item }] títulos que se pueden recomendar (ya sin los vistos ni guardados).
 * featOf:     (kind, item) → { genre, cast[], director[] } de la ficha si se conoce (opcional).
 * Devuelve [{ kind, item, score }] de más a menos afín. Sin semillas o sin afinidad, lista vacía:
 * la fila no se enseña en lugar de rellenarla con algo que no es una recomendación.
 */
export function recommend({ seeds, candidates, limit = 24, maxPerCategory, featOf = () => null, now = Date.now() } = {}) {
    if (!seeds || !seeds.length || !candidates || !candidates.length) return [];
    const fo = (kind, item) => featuresOf(kind, item, featOf(kind, item));
    const p = profile(seeds, fo, now);
    if (!(p.total > 0)) return [];
    const stemRe = stemRegex(p.fran.keys());

    const newest = candidates.reduce((max, c) => Math.max(max, c.item.added || 0), 0);
    const scored = [];
    candidates.forEach((c) => {
        const aff = affinity(c, fo(c.kind, c.item), p, stemRe);
        if (aff <= 0) return;
        // La valoración y la novedad solo desempatan entre títulos igual de afines.
        const rating = Math.min(10, Number(c.item.rating) || 0) / 10;
        const fresh = newest && c.item.added ? Math.max(0, 1 - (newest - c.item.added) / (180 * 86400)) : 0;
        scored.push({ kind: c.kind, item: c.item, score: aff * (1 + rating * 0.6) + rating * 0.08 + fresh * 0.04 });
    });
    scored.sort((a, b) => b.score - a.score || String(a.item.name).localeCompare(String(b.item.name)));

    // Variedad: que una sola categoría no ocupe toda la fila.
    const cap = maxPerCategory || Math.max(3, Math.ceil(limit / 2));
    const perCat = new Map();
    const out = [];
    const skipped = [];
    for (let i = 0; i < scored.length && out.length < limit; i++) {
        const ck = catKey(scored[i].kind, scored[i].item) || scored[i].kind;
        const n = perCat.get(ck) || 0;
        if (n >= cap) { skipped.push(scored[i]); continue; }
        perCat.set(ck, n + 1);
        out.push(scored[i]);
    }
    for (let i = 0; i < skipped.length && out.length < limit; i++) out.push(skipped[i]);
    return out;
}

/** Parecido entre dos conjuntos de características ya calculados (`fa`/`fb`) y las claves de saga de sus títulos. */
function simFeat(fa, fb, franA, franB) {
    let s = 0;
    if (fa.genres.length && fb.genres.length) {
        let shared = 0;
        for (let i = 0; i < fa.genres.length; i++) if (fb.genres.indexOf(fa.genres[i]) !== -1) shared++;
        if (shared) s += 0.7 * shared / Math.sqrt(fa.genres.length * fb.genres.length);
    }
    if (fa.cat && fa.cat === fb.cat) s += 0.35;
    if (fa.director.length && fb.director.length && fa.director.some((d) => fb.director.indexOf(d) !== -1)) s += 1.4;
    if (fa.cast.length && fb.cast.length) s += Math.min(3, fa.cast.slice(0, 6).filter((x) => fb.cast.indexOf(x) !== -1).length) * 0.5;
    if (franA && franA === franB) s += 2;
    return s;
}

/** Parecido de un título con UNO concreto (el de «Porque viste X»): 0 si no tienen nada en común. */
export function similarity(seed, candidate, featOf = () => null) {
    const a = featuresOf(seed.kind, seed.item, featOf(seed.kind, seed.item));
    const b = featuresOf(candidate.kind, candidate.item, featOf(candidate.kind, candidate.item));
    return simFeat(a, b, franOf(seed.item), franOf(candidate.item));
}

/**
 * «Porque viste X»: elige hasta `rows` títulos vistos (positivos y recientes, de sagas distintas) y, para cada uno, los más
 * parecidos que aún no se han visto. Una fila solo sale con `minItems` o más. `seen`: Set de 'kind:id' vistos o guardados.
 */
export function becauseRows({ seeds, candidates, seen = new Set(), rows = 2, perRow = 20, minItems = 6, featOf = () => null, now = Date.now() } = {}) {
    if (!seeds || !seeds.length || !candidates || !candidates.length) return [];
    const usable = seeds
        .map((s) => ({ s, w: seedWeight(s, now) }))
        .filter((x) => x.w >= 1 && (x.s.finished || (Number(x.s.pct) || 0) >= 50))
        .sort((a, b) => (b.s.at || 0) - (a.s.at || 0) || b.w - a.w);
    const out = [];
    const usedFran = new Set();
    const taken = new Set();
    for (const { s } of usable) {
        if (out.length >= rows) break;
        const fk = franchiseKey(s.item.name) || s.kind + ':' + s.item.id;
        if (usedFran.has(fk)) continue;
        const scored = [];
        const fa = featuresOf(s.kind, s.item, featOf(s.kind, s.item));
        const franA = franOf(s.item);
        const stemRe = franA ? stemRegex([franA]) : null;
        for (let i = 0; i < candidates.length; i++) {
            const c = candidates[i];
            if (c.item === s.item) continue;
            const ck = c.kind + ':' + c.item.id;
            if (seen.has(ck) || taken.has(ck)) continue;
            const extra = featOf(c.kind, c.item);
            const fb = featuresOf(c.kind, c.item, extra);
            // Filtro previo barato: sin género, categoría, director ni reparto en común ni nombre de saga, no hay parecido.
            let maybe = !!(fa.cat && fa.cat === fb.cat) || fb.director.length > 0 || fb.cast.length > 0;
            if (!maybe) for (let g = 0; g < fb.genres.length; g++) if (fa.genres.indexOf(fb.genres[g]) !== -1) { maybe = true; break; }
            if (!maybe && stemRe && stemRe.test(textOf(c.item))) maybe = true;
            if (!maybe) continue;
            const sim = simFeat(fa, fb, franA, stemRe && stemRe.test(textOf(c.item)) ? franOf(c.item) : '');
            if (sim < 0.3) continue; // la categoría sola (0,35) basta para sugerir; géneros, director o reparto en común puntúan más y van delante
            const rating = Math.min(10, Number(c.item.rating) || 0) / 10;
            scored.push({ kind: c.kind, item: c.item, score: sim * (1 + rating * 0.5) + rating * 0.05 });
        }
        if (scored.length < minItems) continue;
        scored.sort((a, b) => b.score - a.score || String(a.item.name).localeCompare(String(b.item.name)));
        const items = scored.slice(0, perRow);
        items.forEach((r) => taken.add(r.kind + ':' + r.item.id));
        usedFran.add(fk);
        out.push({ kind: s.kind, seed: s.item, items });
    }
    return out;
}
